import React, { useState, useEffect } from 'react';
import { cn } from '@repo/ui/lib/utils';

import { RangeSlider } from '../../range-slider/component';
import { Button } from '@repo/ui/components/ui/button';

export type SliderControlsProps = {
  onReady?: (data: SliderControlsOnReady) => void;
  from: number;
  to: number;
  filterFrom: number;
  filterTo: number;
  title: string;
  unit?: string;
  onChange: (value: [number, number]) => void;
  className?: string;
};
const STEP = 5;
const DEBOUNCE_TIMEOUT = 500;

export type SliderControlsOnReady = { close: () => void };
export const SliderControl: React.FC<SliderControlsProps> = ({
  from,
  to,
  onChange,
  filterFrom,
  filterTo,
  title,
  unit = 'cm',
  className,
}) => {
  const [hasChangedValues, setHasChangedValues] = useState(false);
  const [initial, setInitial] = useState(true);
  const roundedFrom = Math.floor(filterFrom / STEP) * STEP;
  const roundedTo = Math.ceil(filterTo / STEP) * STEP;

  const roundedMinFrom = Math.floor(from / STEP) * STEP;
  const roundedMaxTo = Math.ceil(to / STEP) * STEP;

  const [sliderValue, setSliderValue] = useState([roundedFrom, roundedTo]);
  const [sliderValueFrom, sliderValueTo] = sliderValue;

  const handleSliderValueChange = (value: [number, number]) => {
    setSliderValue(value);
    setHasChangedValues(true);
  };

  const handleResetClick = () => {
    setSliderValue([roundedMinFrom, roundedMaxTo]);
    setHasChangedValues(false);
  };

  const areMinAndMaxEqual = roundedMinFrom === roundedMaxTo;

  const titleMeasurement = areMinAndMaxEqual
    ? `${title}  (${roundedMinFrom}-${roundedMaxTo}${unit})`
    : `${title} (${unit})`;

  useEffect(() => {
    const getData = setTimeout(() => {
      if (
        (sliderValueTo && sliderValueTo < to) ||
        (sliderValueFrom && sliderValueFrom > from) ||
        !initial
      ) {
        setInitial(false);
        onChange([
          sliderValueFrom || roundedMinFrom,
          sliderValueTo || roundedMaxTo,
        ]);
      }
    }, DEBOUNCE_TIMEOUT);
    return () => clearTimeout(getData);
  }, [sliderValueFrom, sliderValueTo, filterTo, to, filterFrom, from]);

  return (
    <div
      data-testid={`${title}-filter`}
      className={cn('transition-opacity duration-200', className)}
    >
      <p
        data-testid={`${title}-filter-title`}
        className="font-normal text-sm text-cn-foreground"
      >
        Filter by {titleMeasurement}
      </p>
      <RangeSlider
        className={cn('mt-12', {
          hidden: areMinAndMaxEqual,
        })}
        defaultValue={[roundedMinFrom, roundedMaxTo]}
        onValueChange={handleSliderValueChange}
        value={sliderValue}
        leftThumbPopoverText={`${sliderValueFrom}`}
        rightThumbPopoverText={`${sliderValueTo}`}
        minStepsBetweenThumbs={1}
        min={roundedMinFrom}
        max={roundedMaxTo}
        step={STEP}
        data-testid={`${title}-filter-range-slider`}
      />

      <Button
        variant="link"
        role="reset-button"
        className={cn('mb-2 underline p-0 text-xs font-normal', {
          'mt-4': areMinAndMaxEqual,
          'mt-8': !areMinAndMaxEqual,
        })}
        disabled={!hasChangedValues}
        onClick={handleResetClick}
        data-testid={`${title}-filter-reset-button`}
      >
        Reset
      </Button>
    </div>
  );
};
